import { LangCode, UserAttributes } from 'api-client'
import { SingleIncludedUserResourcePermissionResource } from 'api-user-client'
import { assocPath } from 'ramda'

import { Unpromise } from '~shared-components/utils/types'

import { request } from './request'

export const getMe = async (): Promise<{
  authorized: boolean
  me?: SingleIncludedUserResourcePermissionResource
}> => {
  const response = await request<SingleIncludedUserResourcePermissionResource>({
    headers: { 'Content-Type': 'application/json' },
    method: 'GET',
    url: '/api-user/me',
  })

  if (response?.status === 200) {
    return { authorized: true, me: response.data }
  } else if (response?.status === 401) {
    return { authorized: false }
  } else {
    throw new Error('Unable to get user info')
  }
}

export type GetMeResponse = Unpromise<ReturnType<typeof getMe>>

const patchUser = async (
  transform: (
    user: SingleIncludedUserResourcePermissionResource,
  ) => DeepPartial<SingleIncludedUserResourcePermissionResource>,
  user?: SingleIncludedUserResourcePermissionResource,
): Promise<{
  data: SingleIncludedUserResourcePermissionResource | undefined
  error?: boolean
  status: number
}> => {
  if (!user) {
    const { me } = await getMe()

    if (!me) {
      return { data: undefined, error: true, status: 503 }
    }
    user = me
  }

  const newMe = transform(user)

  try {
    return await request<SingleIncludedUserResourcePermissionResource>({
      data: newMe,
      headers: { 'Content-Type': 'application/json' },
      method: 'PATCH',
      params: {
        include: 'permissions',
      },
      url: `/api-user/users/${user.data.id}`,
    })
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Could not reach user API')
    return { data: undefined, error: true, status: 503 }
  }
}

export type PatchUserResponse = Unpromise<ReturnType<typeof patchUser>>

// This function takes the entire user, changes the key to the value and returns the modified user
const changeUserAttribute = <T extends keyof UserAttributes>(
  key: T,
  value: Required<UserAttributes[T]>,
) => assocPath(['data', 'attributes', key])(value)

// This function on the other hand, only returns the modified value for patching a singular value
// At one point Auth0 invalidated sessions when sending along the entire user
const pickOneValue = <T extends keyof UserAttributes>(
  key: T,
  value: Required<UserAttributes[T]>,
) => (
  user: SingleIncludedUserResourcePermissionResource,
): DeepPartial<SingleIncludedUserResourcePermissionResource> => ({
  data: {
    attributes: {
      [key]: value,
    },
    id: user.data.id,
    type: user.data.type,
  },
})

// Promised based patch that throws an error when something goes wrong,
// so a query's onError or onSuccess will work properly
const queryCompatiblePatch = async (
  key: Parameters<typeof pickOneValue>[0],
  value: Parameters<typeof pickOneValue>[1],
  user?: SingleIncludedUserResourcePermissionResource,
  fullReplacement?: boolean,
) => {
  const transformFn = fullReplacement ? changeUserAttribute : pickOneValue
  const response = await patchUser(transformFn(key, value), user)

  if (response.error || response.data === undefined) {
    throw new Error()
  } else {
    return response.data
  }
}
export const setUserLanguage = (language: LangCode) =>
  queryCompatiblePatch('language', language, undefined, true)

export const setAcceptedTerms = () =>
  queryCompatiblePatch('hasAcceptedTerms', true)

export const setLastLoginDate = ({
  date,
  user,
}: {
  date: Date
  user: SingleIncludedUserResourcePermissionResource
}) => queryCompatiblePatch('lastLoggedInAt', date.toISOString(), user)
